import emailjs from '@emailjs/browser';

import { ContactFormData, GoogleSheetsResponse } from '../types';

// Configuration constants
const GOOGLE_SCRIPT_URL =
  'https://script.google.com/macros/s/AKfycbyJDVpNzL_k-R0g4kydjVQSkwDMTRP3vr3xtUXk_42MeHfYiiEf3d0d9BlZQ45sGfov/exec';
const EMAILJS_SERVICE_ID = 'servicios_solinprimejc';
const EMAILJS_TEMPLATE_ID = 'template_2mdsa4b';
const EMAILJS_PUBLIC_KEY = 'H_aENtDe3LO0NUc0X';

/**
 * Contact Service Endpoints
 */
export const contactApi = {
  /**
   * Initialize EmailJS
   */
  init: () => {
    emailjs.init(EMAILJS_PUBLIC_KEY);
  },

  /**
   * Send contact form data via EmailJS
   */
  sendEmail: async (data: ContactFormData) => {
    try {
      const templateParams = {
        from_name: data.name,
        from_email: data.email,
        from_phone: data.phone,
        subject: data.subject,
        message: data.message,
        'g-recaptcha-response': data.recaptchaToken,
      };

      const response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      return {
        success: true,
        data: response,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      console.error('EmailJS Error:', error);
      throw {
        code: 'EMAIL_SEND_FAILED',
        message: 'No se pudo enviar el correo electrónico',
        details: error,
      };
    }
  },

  /**
   * Send contact form data to Google Sheets
   * Uses a hidden iframe approach to avoid CORS issues with Google Apps Script
   * or direct fetch if CORS is enabled on the script
   */
  sendToSheets: async (data: ContactFormData): Promise<GoogleSheetsResponse> => {
    // Prepare data
    const timestamp = new Date().toISOString();
    const params = new URLSearchParams({
      name: data.name,
      email: data.email,
      phone: data.phone,
      subject: data.subject,
      message: data.message,
      timestamp: timestamp,
      recaptchaToken: data.recaptchaToken || '',
    });

    const url = `${GOOGLE_SCRIPT_URL}?${params.toString()}`;

    // Using the iframe approach as in the original code to guarantee delivery despite CORS
    // Wrapping it in a promise to make it awaitable
    return new Promise((resolve, reject) => {
      try {
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        document.body.appendChild(iframe);

        // Declare timeoutId here, but initialize it later to allow cleanup to be defined first
        // eslint-disable-next-line prefer-const
        let timeoutId: ReturnType<typeof setTimeout>;

        const cleanup = () => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
          if (timeoutId) {
            // Ensure timeoutId is assigned before clearing
            clearTimeout(timeoutId);
          }
        };

        iframe.onload = () => {
          // Give it a moment to ensure script execution
          setTimeout(() => {
            cleanup();
            resolve({ result: 'success' });
          }, 1000);
        };

        iframe.onerror = () => {
          cleanup();
          reject({ result: 'error', error: 'Failed to load iframe' });
        };

        iframe.src = url;

        // Fallback timeout - assume success if no error after 5s
        // Google Apps Script often returns opaque responses or blocks CORS,
        // so we can't always know for sure via fetch
        timeoutId = setTimeout(() => {
          cleanup();
          resolve({ result: 'success' });
        }, 5000);
      } catch (error) {
        reject({ result: 'error', error: String(error) });
      }
    });
  },

  /**
   * Submit contact form (sends to both EmailJS and Sheets)
   */
  submit: async (data: ContactFormData) => {
    // Run in parallel for better performance
    const [emailResult, sheetsResult] = await Promise.allSettled([
      contactApi.sendEmail(data),
      contactApi.sendToSheets(data),
    ]);

    const results = {
      email:
        emailResult.status === 'fulfilled'
          ? emailResult.value
          : { success: false, error: emailResult.reason },
      sheets:
        sheetsResult.status === 'fulfilled'
          ? sheetsResult.value
          : { success: false, error: sheetsResult.reason },
    };

    // If both failed, throw error
    if (emailResult.status === 'rejected' && sheetsResult.status === 'rejected') {
      throw {
        code: 'SUBMISSION_FAILED',
        message: 'Error al enviar el formulario. Por favor intente más tarde.',
        details: results,
      };
    }

    return results;
  },
};
